import React, { useEffect, useState, useRef } from 'react';
import { LogOut, ChevronDown, Check, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
type LanguageOption = {
  code: string;
  name: string;
  nativeName: string;
  disabled?: boolean;
};
const languages: LanguageOption[] = [{
  code: 'it',
  name: 'Italian',
  nativeName: 'Italiano'
}, {
  code: 'en',
  name: 'English',
  nativeName: 'English'
}, {
  code: 'fr',
  name: 'French',
  nativeName: 'Français',
  disabled: true
}, {
  code: 'es',
  name: 'Spanish',
  nativeName: 'Español',
  disabled: true
}, {
  code: 'de',
  name: 'German',
  nativeName: 'Deutsch',
  disabled: true
}, {
  code: 'zh',
  name: 'Chinese',
  nativeName: '中文',
  disabled: true
}];
const roleConfig: Record<UserRole, {
  label: string;
  emoji: string;
}> = {
  guest: {
    label: 'Guest',
    emoji: '👋'
  },
  fornitore: {
    label: 'Fornitore',
    emoji: '🧹'
  },
  manutenzione: {
    label: 'Manutenzione',
    emoji: '🔧'
  },
  host: {
    label: 'Host',
    emoji: '🏠'
  }
};
export function Header() {
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  const {
    userRole,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const currentLanguage = languages.find(l => l.code === language) || languages[0];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleLanguageSelect = (lang: LanguageOption) => {
    if (lang.disabled) return;
    setLanguage(lang.code as 'it' | 'en');
    setIsDropdownOpen(false);
  };
  const roleInfo = roleConfig[userRole];
  return <header className="bg-white border-b border-gray-300 fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-95">
      <div className="max-w-4xl mx-auto px-6 h-24 flex items-center justify-between" data-id="element-96">
        {/* Left: Logo */}
        <div className="flex items-center" data-id="element-97">
          <img src="https://cdn.magicpatterns.com/uploads/7iQoZBVrzgoaKtM7t68NLA/CLEANAI_LOGO_V2.png" alt="CleanAI Logo" className="h-10 w-auto object-contain" data-id="element-98" />
        </div>

        {/* Center: Role Badge with Emoji */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2" data-id="element-99">
          <span className="text-base" data-id="element-100">{roleInfo.emoji}</span>
          <span className="text-sm font-semibold text-navy" data-id="element-101">
            {roleInfo.label}
          </span>
        </div>

        {/* Right: Language & Logout */}
        <div className="flex items-center gap-3" data-id="element-102">
          {/* Language Selector Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-103">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-full px-4 py-2 transition-all duration-200 shadow-sm hover:shadow-md group" data-id="element-104">
              <Globe className="w-4 h-4 text-gray-500 group-hover:text-navy transition-colors" data-id="element-105" />
              <span className="text-sm font-medium text-navy" data-id="element-106">
                {currentLanguage.nativeName}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-107" />
            </button>

            <AnimatePresence data-id="element-108">
              {isDropdownOpen && <motion.div initial={{
              opacity: 0,
              y: 8,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 8,
              scale: 0.95
            }} transition={{
              duration: 0.15,
              ease: 'easeOut'
            }} className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden z-50" data-id="element-109">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-110">
                    Select Language
                  </div>
                  {languages.map(lang => <button key={lang.code} onClick={() => handleLanguageSelect(lang)} disabled={lang.disabled} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${lang.disabled ? 'text-gray-300 cursor-not-allowed bg-gray-50/50' : 'text-navy hover:bg-gray-50 cursor-pointer'} ${language === lang.code ? 'bg-gray-50 font-medium' : ''}`} data-id="element-111">
                      <span className={lang.disabled ? 'opacity-50' : ''} data-id="element-112">
                        {lang.nativeName}
                      </span>
                      {language === lang.code && <Check className="w-4 h-4 text-teal-deep" data-id="element-113" />}
                      {lang.disabled && <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded border border-gray-200" data-id="element-114">
                          Soon
                        </span>}
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Logout Button */}
          <button onClick={handleLogout} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-600 hover:text-navy" title={t.common.logout} data-id="element-115">
            <LogOut className="w-5 h-5" data-id="element-116" />
          </button>
        </div>
      </div>
    </header>;
}